import {
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  approveSafetyPpeIssuance,
  createSafetyPpeIssuance,
  deleteSafetyPpeIssuance,
  deleteSafetyPpeIssuanceAttachment,
  downloadSafetyPpeIssuanceAttachment,
  fetchSafetyPpeIssuance,
  fetchSafetyPpeIssuances,
  revokeSafetyPpeIssuance,
  updateSafetyPpeIssuance,
  uploadSafetyPpeIssuanceAttachments,
  downloadSafetyPpeIssuancePdf,
} from "../../api/safety";
import { fetchOrgMembers } from "../../api/org";
import { useCompanyStore } from "../../store/companyStore";
import { APPROVAL_TARGET_SAFETY_PPE } from "../../types/approval";
import { canConfirmInspection } from "../../types/companyRole";
import { OrgMemberSummary } from "../../types/org";
import {
  createEmptySafetyPpeItem,
  defaultSafetyPpeItems,
  parseSafetyPpeItems,
  SafetyPpeIssuance,
  SafetyPpeIssuanceAttachment,
  SafetyPpeItem,
  safetyStatusLabel,
  serializeSafetyPpeItems,
} from "../../types/safety";
import { notifyError, reportError } from "../../utils/notify";
import {
  ApprovalPendingFields,
  isApprovalInProgress,
  withApprovalPendingClass,
  withApprovalPendingLabel,
} from "../../utils/approvalPending";
import ApprovalCancelButton from "../approval/ApprovalCancelButton";
import ApprovalRequestModal from "../approval/ApprovalRequestModal";
import SignaturePadField from "../common/SignaturePadField";

import "../quality/EducationAnnualPlanTab.css";
import "../quality/QualityShell.css";
import "../product/InspectionFormSheet.css";
import "../product/NonconformanceReportPanel.css";
import "../quality/CleanActivityPanel.css";
import "../quality/SmallGroupPanel.css";

function orgMemberName(m: OrgMemberSummary) {
  return m.displayName || m.nickname || `사용자 ${m.userId}`;
}

const UNASSIGNED_DEPT = "__UNASSIGNED__";

function formatSize(size?: number | null) {
  if (size == null) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function apiErrorMessage(err: unknown, fallback: string) {
  const message = (err as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message?.trim() || fallback;
}

function isApproved(status?: string | null) {
  return (status || "").toUpperCase() === "APPROVED";
}

function isDraft(status?: string | null) {
  const s = (status || "DRAFT").toUpperCase();
  return s === "DRAFT" || s === "";
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ["작성", authorName],
          ["검토", reviewerName],
          ["승인", approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span
            style={{
              color: value ? "#1f2a37" : "#98a2b3",
              fontWeight: value ? 600 : 400,
            }}
          >
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

type DocView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; issuanceId: number }
  | { type: "detail"; issuanceId: number };

const LIST_VIEW = { type: "list" } as const;

function SafetyPpeIssuancePanel({
  contractId = null,
  embedded = false,
}: {
  contractId?: number | null;
  embedded?: boolean;
} = {}) {
  void embedded;
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const fileRef = useRef<HTMLInputElement>(null);
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () => Array.from({ length: 12 }, (_, i) => currentYear + 1 - i),
    [currentYear],
  );

  const [view, setView] = useState<DocView>(LIST_VIEW);
  const [rows, setRows] = useState<SafetyPpeIssuance[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filterYear, setFilterYear] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [error, setError] = useState("");
  const [approvalMessage, setApprovalMessage] = useState("");
  const [approvalOpen, setApprovalOpen] = useState(false);

  const [docNo, setDocNo] = useState("");
  const [planYear, setPlanYear] = useState(String(currentYear));
  const [title, setTitle] = useState("");
  const [remark, setRemark] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [items, setItems] = useState<SafetyPpeItem[]>(defaultSafetyPpeItems());
  const [authorName, setAuthorName] = useState<string | null>(null);
  const [reviewerName, setReviewerName] = useState<string | null>(null);
  const [approverName, setApproverName] = useState<string | null>(null);
  const [canRevoke, setCanRevoke] = useState(false);
  const [approvalPending, setApprovalPending] = useState<ApprovalPendingFields>(
    {},
  );
  const [attachments, setAttachments] = useState<SafetyPpeIssuanceAttachment[]>(
    [],
  );
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMemberSummary[]>([]);

  const editId =
    view.type === "edit" || view.type === "detail" ? view.issuanceId : null;
  const showForm = view.type !== "list";
  const isDetail = view.type === "detail";
  const isEditing = view.type === "create" || view.type === "edit";
  const locked = !isEditing;

  const attachmentsLocked =
    isApproved(status) ||
    isApprovalInProgress(approvalPending) ||
    (!isEditing && !isDraft(status));

  const loadOrgMembers = useCallback(async () => {
    try {
      setOrgMembers(await fetchOrgMembers());
    } catch {
      setOrgMembers([]);
    }
  }, []);

  const departmentOptions = useMemo(() => {
    const map = new Map<
      string,
      { key: string; label: string; departmentId: number | null }
    >();
    for (const m of orgMembers) {
      const id = m.departmentId ?? null;
      const label = m.departmentName?.trim() || "미배치";
      const key = id != null ? `d-${id}` : UNASSIGNED_DEPT;
      if (!map.has(key)) map.set(key, { key, label, departmentId: id });
    }
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "ko"),
    );
  }, [orgMembers]);

  const membersByDeptKey = useCallback(
    (deptKey: string) => {
      return orgMembers.filter((m) => {
        const key =
          m.departmentId != null ? `d-${m.departmentId}` : UNASSIGNED_DEPT;
        return key === deptKey;
      });
    },
    [orgMembers],
  );

  const deptKeyOfItem = (item: SafetyPpeItem) => {
    if (item.departmentId != null) return `d-${item.departmentId}`;
    if (!item.departmentName?.trim() || item.departmentName === "미배치") {
      if (item.userId != null) {
        const m = orgMembers.find((x) => x.userId === item.userId);
        if (m)
          return m.departmentId != null
            ? `d-${m.departmentId}`
            : UNASSIGNED_DEPT;
      }
      if (item.departmentName?.trim() === "미배치" || !item.departmentName?.trim())
        return item.departmentName || item.userId ? UNASSIGNED_DEPT : "";
      const byName = departmentOptions.find(
        (d) => d.label === item.departmentName,
      );
      return byName?.key || "";
    }
    const byName = departmentOptions.find(
      (d) => d.label === item.departmentName,
    );
    return byName?.key || "";
  };

  const applyDetail = (detail: SafetyPpeIssuance) => {
    setDocNo(detail.docNo || "");
    setPlanYear(String(detail.planYear || currentYear));
    setTitle(detail.title || "");
    setRemark(detail.remark || "");
    setStatus(detail.status || "DRAFT");
    setItems(parseSafetyPpeItems(detail.itemsJson));
    setAuthorName(detail.authorName ?? null);
    setReviewerName(detail.reviewerName ?? null);
    setApproverName(detail.approverName ?? null);
    setCanRevoke(detail.canRevoke ?? false);
    setApprovalPending({
      approvalInProgress: detail.approvalInProgress,
      pendingApprovalRequestId: detail.pendingApprovalRequestId,
      canCancelApproval: detail.canCancelApproval,
    });
    setAttachments(detail.attachments || []);
  };

  const loadList = useCallback(
    async (keyword?: string, year?: string) => {
      try {
        setLoading(true);
        const yearNum = year?.trim() ? Number(year) : null;
        setRows(
          await fetchSafetyPpeIssuances(
            keyword,
            yearNum != null && !Number.isNaN(yearNum) ? yearNum : null,
            contractId,
          ),
        );
        setError("");
      } catch (err: unknown) {
        reportError(
          setError,
          apiErrorMessage(err, "안전장비 지급현황 목록을 불러오지 못했습니다."),
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [contractId],
  );

  useEffect(() => {
    setView(LIST_VIEW);
    setSearchKeyword("");
    setFilterYear("");
    void loadList();
    void loadOrgMembers();
  }, [companyId, loadList, loadOrgMembers, setView]);

  useEffect(() => {
    if (showForm) return;
    const timer = window.setTimeout(
      () => void loadList(searchKeyword, filterYear),
      250,
    );
    return () => window.clearTimeout(timer);
  }, [searchKeyword, filterYear, loadList, showForm]);

  const resetForm = () => {
    setDocNo("");
    setPlanYear(String(currentYear));
    setTitle(`${currentYear}년 안전장비 지급현황`);
    setRemark("");
    setStatus("DRAFT");
    setItems(defaultSafetyPpeItems());
    setAuthorName(null);
    setReviewerName(null);
    setApproverName(null);
    setCanRevoke(false);
    setApprovalPending({});
    setAttachments([]);
    setPendingFiles([]);
    setApprovalMessage("");
    setApprovalOpen(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const openCreate = () => {
    resetForm();
    setView({ type: "create" });
  };

  const openDetail = async (row: SafetyPpeIssuance) => {
    try {
      setLoading(true);
      setError("");
      const detail = await fetchSafetyPpeIssuance(row.issuanceId);
      applyDetail(detail);
      setPendingFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      setView({ type: "detail", issuanceId: row.issuanceId });
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "안전장비 지급현황을 불러오지 못했습니다."),
      );
    } finally {
      setLoading(false);
    }
  };

  const openEditFromDetail = () => {
    if (editId == null || isApproved(status)) return;
    setView({ type: "edit", issuanceId: editId });
  };

  const backToList = () => {
    resetForm();
    setView(LIST_VIEW);
    void loadList(searchKeyword, filterYear);
  };

  const patchItem = (idx: number, patch: Partial<SafetyPpeItem>) => {
    setItems((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    );
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (locked) {
      reportError(setError, "승인된 지급현황은 수정할 수 없습니다.");
      return;
    }
    if (!title.trim()) {
      reportError(setError, "제목을 입력해 주세요.");
      return;
    }
    const yearNum = Number(planYear);
    if (!yearNum || Number.isNaN(yearNum)) {
      reportError(setError, "연도를 입력해 주세요.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        planYear: yearNum,
        title: title.trim(),
        itemsJson: serializeSafetyPpeItems(items),
        remark: remark.trim() || null,
        contractId: contractId ?? undefined,
      };
      let issuanceId = editId;
      let saved: SafetyPpeIssuance;
      if (issuanceId != null) {
        saved = await updateSafetyPpeIssuance(issuanceId, payload);
      } else {
        saved = await createSafetyPpeIssuance(payload);
        issuanceId = saved.issuanceId;
      }
      if (pendingFiles.length > 0 && issuanceId != null) {
        const uploaded = await uploadSafetyPpeIssuanceAttachments(
          issuanceId,
          pendingFiles,
        );
        saved = {
          ...saved,
          attachments: [...(saved.attachments || []), ...uploaded],
          attachmentCount: (saved.attachmentCount ?? 0) + uploaded.length,
        };
        setPendingFiles([]);
      }
      applyDetail(saved);
      setPendingFiles([]);
      setView({ type: "detail", issuanceId: saved.issuanceId });
      await loadList(searchKeyword, filterYear);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "안전장비 지급현황 저장에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFromList = async (
    row: SafetyPpeIssuance,
    event: MouseEvent,
  ) => {
    event.stopPropagation();
    if (isApproved(row.status)) {
      reportError(setError, "승인된 지급현황은 삭제할 수 없습니다.");
      return;
    }
    if (
      !window.confirm(`"${row.title || row.docNo || "지급현황"}"를 삭제할까요?`)
    )
      return;
    try {
      await deleteSafetyPpeIssuance(row.issuanceId);
      await loadList(searchKeyword, filterYear);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "안전장비 지급현황 삭제에 실패했습니다."),
      );
    }
  };

  const handleApprove = async () => {
    if (editId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "지급현황을 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveSafetyPpeIssuance(editId);
      applyDetail(saved);
      setApprovalMessage("바로 승인되었습니다.");
      await loadList(searchKeyword, filterYear);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (editId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeSafetyPpeIssuance(editId);
      applyDetail(saved);
      setApprovalMessage("승인이 철회되었습니다.");
      await loadList(searchKeyword, filterYear);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (editId == null) return;
    try {
      setPdfDownloading(true);
      const name = docNo?.trim() || String(editId);
      await downloadSafetyPpeIssuancePdf(editId, `보호구지급_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleUploadNow = async (files: FileList | null) => {
    if (!files?.length || attachmentsLocked) return;
    const selected = Array.from(files);
    if (fileRef.current) fileRef.current.value = "";
    if (editId == null) {
      setPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    try {
      setSaving(true);
      const uploaded = await uploadSafetyPpeIssuanceAttachments(
        editId,
        selected,
      );
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 업로드에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (
    attachment: SafetyPpeIssuanceAttachment,
  ) => {
    if (editId == null || attachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      setSaving(true);
      await deleteSafetyPpeIssuanceAttachment(editId, attachment.attachmentId);
      setAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 삭제에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const renderItems = (editable: boolean) => (
    <div className="ca-items">
      {items.map((item, idx) => (
        <div className="ca-item-card" key={item.id}>
          <div className="ca-item-card__head">
            <strong>항목 {idx + 1}</strong>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {editable ? (
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  onClick={() =>
                    patchItem(idx, {
                      manualEntry: !item.manualEntry,
                      userId: null,
                      departmentId: null,
                      employeeName: item.manualEntry ? "" : item.employeeName,
                      departmentName: item.manualEntry
                        ? ""
                        : item.departmentName,
                    })
                  }
                >
                  {item.manualEntry ? "조직에서 선택" : "직접 입력"}
                </button>
              ) : null}
              {editable ? (
                <button
                  type="button"
                  className="ncr-btn is-danger"
                  onClick={() =>
                    setItems((prev) => prev.filter((_, i) => i !== idx))
                  }
                >
                  삭제
                </button>
              ) : null}
            </div>
          </div>
          <div className="ca-item-card__grid">
            <label className="is-wide">
              장비명
              <input
                value={item.equipmentName}
                onChange={(e) =>
                  patchItem(idx, { equipmentName: e.target.value })
                }
                placeholder="안전모, 안전화 등"
                disabled={!editable}
              />
            </label>
            <label>
              수량
              <input
                value={item.qty}
                onChange={(e) => patchItem(idx, { qty: e.target.value })}
                placeholder="1"
                disabled={!editable}
              />
            </label>
            <label>
              지급일
              <input
                type="date"
                value={item.issueDate}
                onChange={(e) =>
                  patchItem(idx, { issueDate: e.target.value })
                }
                disabled={!editable}
              />
            </label>
            <label className="is-wide">
              부서
              {!editable || item.manualEntry ? (
                <input
                  value={item.departmentName || ""}
                  readOnly={!editable}
                  placeholder="부서"
                  onChange={(e) =>
                    patchItem(idx, {
                      departmentName: e.target.value,
                      manualEntry: true,
                      userId: null,
                      departmentId: null,
                    })
                  }
                />
              ) : (
                <select
                  value={deptKeyOfItem(item)}
                  onChange={(e) => {
                    const key = e.target.value;
                    const opt = departmentOptions.find((d) => d.key === key);
                    patchItem(idx, {
                      departmentId: opt?.departmentId ?? null,
                      departmentName: opt?.label || "",
                      userId: null,
                      employeeName: "",
                      manualEntry: false,
                    });
                  }}
                >
                  <option value="">부서 선택</option>
                  {departmentOptions.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.label}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label className="is-wide">
              수령자
              {!editable || item.manualEntry ? (
                <input
                  value={item.employeeName}
                  readOnly={!editable}
                  placeholder="수령자"
                  onChange={(e) =>
                    patchItem(idx, {
                      employeeName: e.target.value,
                      manualEntry: true,
                      userId: null,
                    })
                  }
                />
              ) : (
                <select
                  value={item.userId != null ? String(item.userId) : ""}
                  disabled={!deptKeyOfItem(item)}
                  onChange={(e) => {
                    const uid = e.target.value ? Number(e.target.value) : null;
                    if (uid == null) {
                      patchItem(idx, { userId: null, employeeName: "" });
                      return;
                    }
                    const m = orgMembers.find((x) => x.userId === uid);
                    if (!m) return;
                    patchItem(idx, {
                      userId: m.userId,
                      employeeName: orgMemberName(m),
                      departmentId: m.departmentId ?? null,
                      departmentName: m.departmentName?.trim() || "미배치",
                      manualEntry: false,
                    });
                  }}
                >
                  <option value="">
                    {deptKeyOfItem(item)
                      ? "수령자 선택"
                      : "부서 먼저 선택"}
                  </option>
                  {deptKeyOfItem(item)
                    ? membersByDeptKey(deptKeyOfItem(item)).map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {orgMemberName(m)}
                          {m.jobTitle ? ` · ${m.jobTitle}` : ""}
                        </option>
                      ))
                    : null}
                </select>
              )}
            </label>
            <label className="is-full">
              비고
              <textarea
                rows={2}
                value={item.remark}
                onChange={(e) => patchItem(idx, { remark: e.target.value })}
                placeholder="비고"
                disabled={!editable}
              />
            </label>
            <div className="is-full">
              <SignaturePadField
                label="수령 서명"
                value={item.signatureData}
                disabled={!editable}
                onChange={(signatureData) =>
                  patchItem(idx, { signatureData })
                }
              />
            </div>
          </div>
        </div>
      ))}
      {items.length === 0 ? (
        <p className="quality-form__hint">등록된 지급 항목이 없습니다.</p>
      ) : null}
    </div>
  );

  const renderDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void handleDownloadPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={saving || approving}
          onClick={() => void handleRevoke()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {isDraft(status) && !isApprovalInProgress(approvalPending) ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={openEditFromDetail}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={saving || approving}
            onClick={() => {
              setApprovalMessage("");
              setApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={saving || approving}
              onClick={() => void handleApprove()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={saving || approving}
            onClick={() => {
              if (editId == null) return;
              if (
                !window.confirm(
                  `"${title || docNo || "지급현황"}"를 삭제할까요?`,
                )
              )
                return;
              void deleteSafetyPpeIssuance(editId).then(() => {
                backToList();
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      {isApprovalInProgress(approvalPending) ? (
        <ApprovalCancelButton
          detail={approvalPending}
          className="ncr-btn is-ghost"
          disabled={saving || approving}
          onCancelled={async () => {
            if (editId == null) return;
            const detail = await fetchSafetyPpeIssuance(editId);
            applyDetail(detail);
            void loadList(searchKeyword, filterYear);
          }}
        />
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onClick={backToList}
      >
        목록
      </button>
    </div>
  );

  const renderEditActions = () => (
    <div className="quality-form__actions">
      <button
        type="submit"
        className="ncr-btn is-primary"
        disabled={saving || approving}
      >
        {saving ? "저장 중..." : "저장"}
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onClick={() =>
          view.type === "edit" && editId != null
            ? setView({ type: "detail", issuanceId: editId })
            : backToList()
        }
      >
        {view.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  if (showForm) {
    return (
      <main className="quality-shell sg-panel">
        <form
          className="quality-form"
          onSubmit={
            isEditing ? (e) => void handleSave(e) : (e) => e.preventDefault()
          }
        >
          {isDetail ? (
            <div
              className="quality-shell__toolbar"
              style={{ marginBottom: 12 }}
            >
              <div>
                <h3 style={{ margin: 0 }}>안전장비 지급현황 상세</h3>
                <p className="quality-form__hint" style={{ margin: "4px 0 0" }}>
                  {docNo || "-"} · {title || "-"} ·{" "}
                  {withApprovalPendingLabel(
                    safetyStatusLabel(status),
                    approvalPending.approvalInProgress,
                  )}
                </p>
              </div>
              {renderDetailActions()}
            </div>
          ) : (
            renderEditActions()
          )}

          {error ? <p className="quality-error">{error}</p> : null}
          {approvalMessage ? (
            <p className="quality-form__hint">{approvalMessage}</p>
          ) : null}

          <section className="quality-form__section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                alignItems: "flex-start",
                marginBottom: 12,
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>
                  {view.type === "create"
                    ? "안전장비 지급현황 등록"
                    : view.type === "edit"
                      ? "안전장비 지급현황 수정"
                      : "안전장비 지급현황"}
                </h3>
                {isEditing ? (
                  <p
                    className="quality-form__hint"
                    style={{ margin: "4px 0 0" }}
                  >
                    저장하면 상세보기로 이동합니다.
                  </p>
                ) : null}
              </div>
              <StampBox
                authorName={authorName}
                reviewerName={reviewerName}
                approverName={approverName}
              />
            </div>
            <div className="quality-form__grid">
              <label>
                문서번호
                <input
                  value={
                    docNo ||
                    (view.type === "create" ? "저장 시 자동 채번" : "-")
                  }
                  readOnly
                />
              </label>
              <label>
                연도 *
                <select
                  value={planYear}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPlanYear(next);
                    if (
                      !title.trim() ||
                      title.endsWith("년 안전장비 지급현황")
                    ) {
                      setTitle(`${next}년 안전장비 지급현황`);
                    }
                  }}
                  disabled={locked}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
              <label className="is-span-2">
                제목 *
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="지급현황 제목"
                  maxLength={255}
                  required
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                비고
                <textarea
                  rows={3}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="메모"
                  maxLength={2000}
                  readOnly={locked}
                />
              </label>
            </div>
          </section>

          <section className="quality-form__section">
            <div className="ca-section-head">
              <h3>지급 항목</h3>
              {isEditing ? (
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  onClick={() =>
                    setItems((prev) => [...prev, createEmptySafetyPpeItem()])
                  }
                >
                  항목 추가
                </button>
              ) : null}
            </div>
            {renderItems(isEditing)}
          </section>

          <section className="quality-form__section">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0 }}>첨부파일</h3>
              {!attachmentsLocked ? (
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  disabled={saving || approving}
                  onClick={() => fileRef.current?.click()}
                >
                  파일 추가
                </button>
              ) : null}
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                onChange={(e) => void handleUploadNow(e.target.files)}
              />
            </div>
            <p className="quality-form__hint">
              관련 파일을 첨부할 수 있습니다. (최대 20개, 각 20MB)
              {editId == null ? " 신규는 저장 시 함께 업로드됩니다." : ""}
            </p>
            <ul className="edu-plan-files">
              {attachments.map((file) => (
                <li key={file.attachmentId}>
                  <div>
                    <strong>{file.fileName}</strong>
                    <span>{formatSize(file.fileSize)}</span>
                  </div>
                  <div className="edu-plan-files__actions">
                    <button
                      type="button"
                      className="ncr-btn is-secondary"
                      onClick={() =>
                        void downloadSafetyPpeIssuanceAttachment(
                          editId!,
                          file.attachmentId,
                          file.fileName,
                        ).catch(() =>
                          reportError(
                            setError,
                            "첨부파일 다운로드에 실패했습니다.",
                          ),
                        )
                      }
                    >
                      다운로드
                    </button>
                    {!attachmentsLocked ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        disabled={saving || approving}
                        onClick={() => void handleDeleteAttachment(file)}
                      >
                        삭제
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
              {pendingFiles.map((file, idx) => (
                <li key={`pending-${file.name}-${idx}`}>
                  <div>
                    <strong>{file.name}</strong>
                    <span>저장 대기 · {formatSize(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    className="ncr-btn is-danger"
                    onClick={() =>
                      setPendingFiles((prev) =>
                        prev.filter((_, i) => i !== idx),
                      )
                    }
                  >
                    제거
                  </button>
                </li>
              ))}
              {attachments.length === 0 && pendingFiles.length === 0 ? (
                <li className="is-empty">등록된 첨부파일이 없습니다.</li>
              ) : null}
            </ul>
          </section>

          {isDetail ? (
            <div className="quality-form__actions">{renderDetailActions()}</div>
          ) : (
            renderEditActions()
          )}

          {editId != null ? (
            <ApprovalRequestModal
              open={approvalOpen}
              titleDefault={`${title || "안전장비 지급현황"} 결재`}
              targetType={APPROVAL_TARGET_SAFETY_PPE}
              targetId={editId}
              onClose={() => setApprovalOpen(false)}
              onSubmitted={() => {
                setApprovalOpen(false);
                setApprovalMessage(
                  "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                );
                if (editId != null) {
                  void fetchSafetyPpeIssuance(editId).then(applyDetail);
                }
                void loadList(searchKeyword, filterYear);
              }}
            />
          ) : null}
        </form>
      </main>
    );
  }

  return (
    <main className="quality-shell sg-panel">
      <div className="quality-shell__toolbar">
        <div>
          <h2>
            {contractId != null
              ? "계약 안전장비 지급현황"
              : "안전장비 지급현황"}
          </h2>
          <p>연도별 안전장비 지급 내역을 작성·결재합니다.</p>
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            style={{
              height: 34,
              padding: "0 10px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            <option value="">전체 연도</option>
            {yearOptions.map((y) => (
              <option key={y} value={String(y)}>
                {y}년
              </option>
            ))}
          </select>
          <input
            type="search"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="문서번호·제목 검색"
            style={{
              height: 34,
              padding: "0 12px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              minWidth: 180,
              fontSize: 13,
            }}
          />
          <button
            type="button"
            className="ncr-btn is-primary"
            onClick={openCreate}
          >
            등록
          </button>
        </div>
      </div>

      {error ? <p className="quality-error">{error}</p> : null}

      <div className="quality-table-wrap">
        <table className="quality-table">
          <thead>
            <tr>
              <th style={{ width: 140 }}>문서번호</th>
              <th style={{ width: 88 }}>연도</th>
              <th>제목</th>
              <th style={{ width: 100 }}>작성자</th>
              <th style={{ width: 72 }}>첨부</th>
              <th style={{ width: 88 }}>상태</th>
              <th style={{ width: 80 }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7}>불러오는 중…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7}>등록된 안전장비 지급현황이 없습니다.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.issuanceId}
                  onClick={() => void openDetail(row)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{row.docNo || "-"}</td>
                  <td>{row.planYear ?? "-"}</td>
                  <td>{row.title || "-"}</td>
                  <td>{row.authorName || "-"}</td>
                  <td>{row.attachmentCount ?? 0}</td>
                  <td>
                    <span
                      className={`quality-badge is-${withApprovalPendingClass(
                        isApproved(row.status) ? "approved" : "draft",
                        row.approvalInProgress,
                      )}`}
                    >
                      {withApprovalPendingLabel(
                        safetyStatusLabel(row.status),
                        row.approvalInProgress,
                      )}
                    </span>
                  </td>
                  <td>
                    {!isApproved(row.status) && !isApprovalInProgress(row) ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        onClick={(e) => void handleDeleteFromList(row, e)}
                      >
                        삭제
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}

export default SafetyPpeIssuancePanel;
